import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  NJButton,
  NJCard,
  NJHeading,
  NJIcon,
  NJInlineMessage,
  NJInputSearch,
  NJPagination,
  NJSpinner,
  NJTag,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { notifySuccess } from "@/api/apiErrorStore";
import { FilterChip } from "@/components/store/FilterChip";
import { MetaTagList } from "@/components/store/MetaTagList";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import type { Skill } from "@/types";
import { SkillCompatibilityBrandIcon } from "./SkillCompatibilityBrandIcon";
import { SkillCompatibilityTags } from "./SkillCompatibilityTags";
import { SkillProposalForm } from "./SkillProposalForm";
import {
  downloadSkill,
  useSkillCategories,
  useSkillHarnesses,
  useSkills,
} from "./useSkills";

const PAGE_SIZE = 12;

function SkillCard({ skill }: { skill: Skill }) {
  const navigate = useNavigate();
  return (
    <NJCard
      onClick={() => navigate(`/skills/${skill.slug}`)}
      className="!mb-0 flex cursor-pointer flex-col gap-3 p-5 shadow-sm transition hover:border-nj-brand-moderate-default hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <NJHeading as="h2" scale="md">
          {skill.name}
        </NJHeading>
        <NJTag label={skill.sourceType} variant="grey" />
      </div>

      <div className="flex flex-col gap-1.5">
        <NJText
          as="span"
          scale="xs"
          className="font-nj-bold uppercase tracking-wide text-nj-neutral-secondary-default"
        >
          Compatible with
        </NJText>
        <SkillCompatibilityTags
          items={skill.compatibleHarnesses ?? []}
          max={3}
        />
      </div>
      <MetaTagList
        label="Categories"
        items={skill.includedSkills ?? []}
        max={3}
      />

      <NJText
        as="p"
        scale="sm"
        className="min-h-[48px] text-nj-neutral-secondary-default"
      >
        {skill.description}
      </NJText>

      <div className="mt-auto flex items-end justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <NJText
            as="span"
            scale="xs"
            className="text-nj-neutral-secondary-default"
          >
            Creator <strong>{skill.createdByName || "-"}</strong>
          </NJText>
          <NJText
            as="span"
            scale="xs"
            className="text-nj-neutral-secondary-default"
          >
            {skill.currentVersion
              ? `Current version ${skill.currentVersion}`
              : "No version yet"}
          </NJText>
        </div>
        <div
          className="flex items-center gap-2"
          onClick={(event) => event.stopPropagation()}
        >
          {skill.sourceType === "GITHUB" ? (
            <NJButton
              label="Open"
              icon="open_in_new"
              emphasis="subtle"
              size="small"
              disabled={!skill.repositoryUrl}
              onClick={() =>
                skill.repositoryUrl &&
                window.open(skill.repositoryUrl, "_blank", "noreferrer")
              }
            />
          ) : (
            <NJButton
              label="Download"
              icon="download"
              emphasis="subtle"
              size="small"
              disabled={!skill.currentVersion}
              onClick={() => void downloadSkill(skill.slug)}
            />
          )}
        </div>
      </div>
    </NJCard>
  );
}

/** Skill store — server-side search, harness/category filters and pagination. */
export function SkillStorePage() {
  const [searchInput, setSearchInput] = useState("");
  const [harness, setHarness] = useState<string>("all");
  const [category, setCategory] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [showProposalForm, setShowProposalForm] = useState(false);

  const query = useDebouncedValue(searchInput);

  useEffect(() => setPage(1), [query, harness, category]);

  const { data, isLoading, isError } = useSkills({
    q: query,
    harness: harness === "all" ? undefined : harness,
    category: category === "all" ? undefined : category,
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  });
  const { data: harnesses } = useSkillHarnesses();
  const { data: categories } = useSkillCategories();

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1),
    [data],
  );

  return (
    <div className="flex w-full flex-col">
      <header className="flex flex-col gap-5 border-b border-nj-neutral-subtle-default pb-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-nj-brand-secondary-default text-nj-brand-solid-default">
              <NJIcon name="inventory_2" />
            </span>
            <div className="flex flex-col">
              <NJHeading as="h1" scale="lg">
                Skill store
              </NJHeading>
              <NJText
                as="p"
                scale="sm"
                className="text-nj-neutral-secondary-default"
              >
                Discover reusable skills, shipped as ZIP packages or GitHub
                repositories.
              </NJText>
            </div>
          </div>
          <NJButton
            label={showProposalForm ? "Hide form" : "Propose a skill"}
            emphasis="subtle"
            onClick={() => setShowProposalForm((prev) => !prev)}
          />
        </div>

        {showProposalForm && (
          <SkillProposalForm
            onSubmitted={(name) => {
              setShowProposalForm(false);
              notifySuccess(
                `The skill “${name}” has been submitted and is awaiting an administrator’s validation. It will appear in the store once approved.`,
              );
            }}
          />
        )}

        <div className="flex flex-col gap-3">
          <NJInputSearch
            label="Search skills"
            placeholder="Search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.currentTarget.value)}
          />

          <div className="flex flex-col gap-3">
            {harnesses && harnesses.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <NJText
                  as="span"
                  scale="sm"
                  className="min-w-[118px] font-nj-bold"
                >
                  Compatible with
                </NJText>
                <div className="flex flex-wrap gap-2">
                  {["all", ...harnesses].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setHarness(value)}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition ${
                        harness === value
                          ? "border-nj-brand-solid-default bg-nj-brand-secondary-default text-nj-brand-solid-default"
                          : "border-nj-neutral-subtle-default text-nj-neutral-secondary-default hover:border-nj-brand-moderate-default"
                      }`}
                    >
                      {value !== "all" && (
                        <SkillCompatibilityBrandIcon value={value} />
                      )}
                      {value === "all" ? "All" : value}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {categories && categories.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <NJText
                  as="span"
                  scale="sm"
                  className="min-w-[118px] font-nj-bold"
                >
                  Categories
                </NJText>
                <div className="flex flex-wrap gap-2">
                  <FilterChip
                    active={category === "all"}
                    label="All"
                    onClick={() => setCategory("all")}
                  />
                  {categories.map((value) => (
                    <FilterChip
                      key={value}
                      active={category === value}
                      label={value}
                      onClick={() => setCategory(value)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <section className="pt-5">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <NJSpinner aria-label="Loading the skill catalogue" />
          </div>
        ) : isError ? (
          <NJInlineMessage variant="error">
            Could not load the skill catalogue.
          </NJInlineMessage>
        ) : (
          <>
            <div className="mx-auto grid max-w-[1240px] gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
              {(data?.items ?? []).map((skill) => (
                <SkillCard key={skill.slug} skill={skill} />
              ))}
            </div>

            {data && data.items.length === 0 && (
              <NJText
                as="p"
                scale="sm"
                className="mx-auto max-w-[1240px] text-nj-neutral-secondary-default"
              >
                No skills match this search.
              </NJText>
            )}

            {totalPages > 1 && (
              <div className="mt-[18px] flex justify-center">
                <NJPagination
                  count={totalPages}
                  currentItem={page}
                  ariaLabelPrevious="Previous skills page"
                  ariaLabelNext="Next skills page"
                  onChange={(item: number) => setPage(item)}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
